// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react'
import { useTranslation } from 'react-i18next';

import "./ZoomImage.scss"
import { AuthContext } from '../../context/AuthContext.js';
import { storage } from '../../context/FirebaseConfig.js';
import { fetchImageUrl } from "../../shared_components/utils/ImgUtils.js"
import LoadingIcon from '../LoadingIcon/LoadingIcon.jsx';

export default function ZoomImage({ image_url }) {
    const { t } = useTranslation();
    const { currentUser } = React.useContext(AuthContext);
    const [imageData, setImageData] = React.useState(null);
    const [zoomIn, setZoomIn] = React.useState(false);
    const [showGrid, setShowGrid] = React.useState(false);
    const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
    const imgRef = React.useRef(null);

    const calculateImageSize = React.useCallback(() => {
        if (imageData && imgRef.current) {
            const container = imgRef.current.parentElement;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imageAspectRatio = imgRef.current.naturalWidth / imgRef.current.naturalHeight;
            
            let width, height;
            if (imageAspectRatio > containerWidth / containerHeight) {
                width = containerWidth;
                height = containerWidth / imageAspectRatio;
            } else {
                height = containerHeight;
                width = containerHeight * imageAspectRatio;
            }
            
            setImageSize({ width, height });
        }
    }, [imageData]);

    React.useEffect(() => {
        /* Load the image on url change */
        setImageData(null);
        if (!image_url) {
            return;
        }

        fetchImageUrl(storage, image_url).then((url) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                setImageData(img);
            }
        })
    }, [image_url])

    React.useEffect(() => {
        calculateImageSize();
    }, [calculateImageSize])

    /* Recalculate image size on window resize */
    React.useEffect(() => {
        window.addEventListener('resize', calculateImageSize);
        return () => window.removeEventListener('resize', calculateImageSize);
    }, [calculateImageSize]);

    const handleMouseMove = (e) => {
        /* See how the mouse moved in order to pan zoomed image */
        const { left, top, width, height } = imgRef.current.getBoundingClientRect();
        const x = ((e.pageX - left) / width) * 100;
        const y = ((e.pageY - window.scrollY - top) / height) * 100;
        imgRef.current.style.transformOrigin = `${x}% ${y}%`;
    };

    // Calculate grid positions based on image size
    const gridStyle = {
        width: `${imageSize.width}px`,
        height: `${imageSize.height}px`,
        left: `calc(50% - ${imageSize.width / 2}px)`,
        top: `calc(50% - ${imageSize.height / 2}px)`
    };
    
    return (
        <div className="zoom-img-container" onMouseMove={handleMouseMove}>
            {showGrid && imageData && (
                <div className="grid-overlay" style={gridStyle}>
                    <div className="grid-line horizontal" style={{ top: '33.33%' }}></div>
                    <div className="grid-line horizontal" style={{ top: '66.66%' }}></div>
                    <div className="grid-line vertical" style={{ left: '33.33%' }}></div>
                    <div className="grid-line vertical" style={{ left: '66.66%' }}></div>
                </div>
            )}
            <img 
                ref={imgRef}
                className={`zoom-img ${zoomIn ? 'zoomed' : ''}`}  
                src={imageData?.src ?? ""} 
                style={{ display: imageData ? 'block' : 'none' }}
                onClick={() => setZoomIn((prev) => !prev)}
            />
            {/* Show loading icon when the image data has not loaded yet */}
            {!imageData && <div className="loading-icon-container"><LoadingIcon /></div>}
            {/* Add button to toggle grid (only to people with labeler flag) */}
            {currentUser.info.user_flags && ['labeler', 'admin'].some(flag => currentUser.info.user_flags.includes(flag)) && 
            <button 
                className={`grid-toggle-button ${showGrid ? 'active' : ''}`}
                onClick={() => setShowGrid(prev => !prev)}
                title={showGrid ? t("grid_hide") : t("grid_show")}
            >
                <div className="grid-icon"></div>
            </button>}
        </div>
    )
}